import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  ErrorOutline,
  FolderZip,
  HighlightOff,
  ImageOutlined,
  InsertDriveFile,
} from "@mui/icons-material";

/** Placeholder docs when API does not return a list yet (aligned with Asset Review Figma) */
const PLACEHOLDER_REJECT_DOCS = [
  {
    id: "1",
    name: "Title Deed.pdf",
    category: "Title Deed",
    size: "2.4 MB",
    date: "2024-11-01",
    kind: "pdf",
    apiType: "titleDeed",
  },
  {
    id: "2",
    name: "Valuation Report.pdf",
    category: "Financial",
    size: "1.8 MB",
    date: "2024-11-02",
    kind: "pdf",
    apiType: "valuationReport",
  },
  {
    id: "3",
    name: "Property Photos.zip",
    category: "Media",
    size: "13.2 MB",
    date: "2024-11-02",
    kind: "zip",
    apiType: "propertyPhotos",
  },
  {
    id: "4",
    name: "Insurance Certificate.pdf",
    category: "Insurance",
    size: "890 KB",
    date: "2024-11-03",
    kind: "pdf",
    apiType: "insuranceCertificate",
  },
  {
    id: "5",
    name: "Ownership Verification.pdf",
    category: "Verification",
    size: "1.1 MB",
    date: "2024-11-03",
    kind: "pdf",
    apiType: "ownershipVerification",
  },
  {
    id: "6",
    name: "Tax Documents.pdf",
    category: "Financial",
    size: "3.2 MB",
    date: "2024-11-04",
    kind: "pdf",
    apiType: "spvDocument",
  },
];

export function formatFileSizeBytes(bytes) {
  if (bytes == null || bytes === "") return "—";
  const b = Number(bytes);
  if (!Number.isFinite(b) || b < 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(b < 10240 ? 1 : 0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/** Admin API `documents[].type` → display category */
function adminDocTypeLabel(type) {
  const t = String(type || "").toLowerCase();
  if (t === "spvdocument" || t === "spv_document") return "SPV Document";
  if (t === "titledeed" || t === "title_deed") return "Title Deed";
  if (t === "valuationreport" || t === "valuation_report") return "Financial";
  return type ? String(type).replace(/([A-Z])/g, " $1").trim() : "Document";
}

function fileKindFromApi(fileType, fileName) {
  const ft = String(fileType || "").toLowerCase();
  if (ft === "zip") return "zip";
  if (ft === "png" || ft === "jpg" || ft === "jpeg" || ft === "gif" || ft === "webp") {
    return "image";
  }
  const n = String(fileName || "").toLowerCase();
  if (n.endsWith(".zip")) return "zip";
  if (/\.(png|jpe?g|gif|webp)$/.test(n)) return "image";
  return "pdf";
}

function normalizeFileEntry(f) {
  if (!f || typeof f !== "object") return null;
  return {
    ...f,
    _id: f._id ?? f.id,
    fileUrl: f.signedUrl || f.fileUrl || f.url,
    type: f.type ?? f.documentType ?? f.docType,
    fileType: f.fileType,
    fileSize: f.fileSize,
    documentName: f.documentName ?? f.name ?? f.fileName,
  };
}

function flattenNestedDocumentationAndMore(raw) {
  const out = [];
  const doc = raw?.documentation;
  if (doc && typeof doc === "object" && !Array.isArray(doc)) {
    const push = (entry, defaultType, idKey) => {
      if (!entry || typeof entry !== "object") return;
      const url = entry.fileUrl || entry.signedUrl || entry.url;
      const name = entry.documentName || entry.fileName || entry.name;
      if (!url && !name) return;
      out.push({
        ...entry,
        _id: entry._id || idKey,
        type: entry.type || defaultType,
        fileUrl: entry.signedUrl || entry.fileUrl || entry.url,
      });
    };
    push(doc.titleDeed, "titleDeed", "doc-titleDeed");
  }

  const vr = raw?.financialDetails?.valuationReport;
  if (vr && typeof vr === "object") {
    const url = vr.signedUrl || vr.fileUrl || vr.url;
    if (url || vr.documentName) {
      out.push({
        ...vr,
        _id: vr._id || "doc-valuationReport",
        type: vr.type || "valuationReport",
        fileUrl: url,
      });
    }
  }

  const spv = raw?.spvDetails || {};
  if (spv.spvSignature && typeof spv.spvSignature === "object") {
    const s = spv.spvSignature;
    const url = s.signedUrl || s.fileUrl || s.url;
    if (url || s.documentName) {
      out.push({
        ...s,
        _id: s._id || "doc-spvSignature",
        type: s.type || "spvSignature",
        fileUrl: url,
      });
    }
  }
  (spv.spvDocuments || []).forEach((item, i) => {
    if (!item || typeof item !== "object") return;
    const url = item.signedUrl || item.fileUrl || item.url;
    if (url || item.documentName) {
      out.push({
        ...item,
        _id: item._id || `doc-spv-${i}`,
        type: item.type || "spvDocument",
        fileUrl: url,
      });
    }
  });

  return out;
}

/**
 * Map API documents (admin assets + generic shapes) for review / reject modals.
 * @param {object} assetRow — row with { raw } (full API asset) and optional { owner, name, id }
 */
export function getRejectDocuments(assetRow) {
  const raw = assetRow?.raw;
  if (!raw || typeof raw !== "object") {
    return PLACEHOLDER_REJECT_DOCS;
  }

  let source = null;

  if (Array.isArray(raw.documents)) {
    if (raw.documents.length === 0) return [];
    source = raw.documents;
  } else if (Array.isArray(raw.files)) {
    if (raw.files.length === 0) return [];
    source = raw.files.map(normalizeFileEntry).filter(Boolean);
  } else if (Array.isArray(raw.documentation)) {
    if (raw.documentation.length === 0) return [];
    source = raw.documentation;
  } else {
    source = flattenNestedDocumentationAndMore(raw);
  }

  if (!source || source.length === 0) {
    return PLACEHOLDER_REJECT_DOCS;
  }

  return source.map((d, i) => {
    const rawDate =
      d.uploadedAt || d.date || d.createdAt || raw?.submitted || raw?.submittedAt || raw?.createdAt;
    let dateStr = "—";
    if (rawDate) {
      try {
        dateStr = new Date(rawDate).toISOString().slice(0, 10);
      } catch {
        dateStr = String(rawDate).slice(0, 10);
      }
    }
    const name =
      d.documentName ||
      d.name ||
      d.fileName ||
      d.title ||
      (d.fileType ? `${adminDocTypeLabel(d.type)}.${d.fileType}` : `${adminDocTypeLabel(d.type)}`);
    const sizeDisplay =
      typeof d.fileSize === "number"
        ? formatFileSizeBytes(d.fileSize)
        : d.size || d.fileSize || "—";
    const kind = fileKindFromApi(d.fileType, name);
    const rawType = d.type ?? d.documentType ?? d.docType;
    const apiType = rawType != null && String(rawType).trim() !== "" ? String(rawType).trim() : "";
    return {
      id: String(d._id || d.id || `doc-${i}`),
      name,
      category: adminDocTypeLabel(d.type),
      size: sizeDisplay,
      date: dateStr,
      kind,
      url: d.fileUrl || d.url || d.previewUrl || "",
      apiType,
    };
  });
}

export function RejectAssetModal({ open, onClose, asset, onConfirm, errorMessage }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const docs = useMemo(() => (asset ? getRejectDocuments(asset) : []), [asset]);
  const hasDocSelection = selectedIds.size > 0;

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setReason("");
      setSubmitting(false);
    }
  }, [open, asset?.id]);

  const toggleDoc = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const requestClose = () => {
    if (!submitting) onClose();
  };

  const handleConfirm = async () => {
    if (!onConfirm) {
      requestClose();
      return;
    }
    if (!hasDocSelection) return;
    setSubmitting(true);
    try {
      const filesNeedToBeUpdated = Array.from(selectedIds)
        .map((id) => docs.find((doc) => doc.id === id)?.apiType)
        .filter((t) => t != null && String(t).trim() !== "");
      await onConfirm(asset, {
        filesNeedToBeUpdated,
        reason: reason.trim(),
      });
      onClose();
    } catch {
      /* parent handles error; keep modal open */
    } finally {
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (submitting) return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutline sx={{ color: "error.main", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={20} sx={{ color: "error.main" }}>
                Reject Asset Listing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Select at least one document with issues and provide a reason for rejection.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={requestClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ bgcolor: "background.subtle", borderRadius: 2, p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Owner
            </Typography>
            <Typography fontWeight={600}>{asset.owner}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Asset
            </Typography>
            <Typography fontWeight={700}>{asset.name}</Typography>
          </Box>

          <Typography fontWeight={700} fontSize={14}>
            Select Documents with Issues{" "}
            <Box component="span" sx={{ color: "error.main" }}>
              *
            </Box>
          </Typography>
          <FormGroup sx={{ maxHeight: 220, overflow: "auto", pr: 1 }}>
            {docs.map((d) => (
              <Paper
                key={d.id}
                variant="outlined"
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: selectedIds.has(d.id) ? "error.main" : "border.light",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIds.has(d.id)}
                      onChange={() => toggleDoc(d.id)}
                      color="error"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ ml: 0.5 }}>
                      {d.kind === "zip" ? (
                        <FolderZip sx={{ color: "stats.amber" }} />
                      ) : d.kind === "image" ? (
                        <ImageOutlined sx={{ color: "stats.green" }} />
                      ) : (
                        <InsertDriveFile sx={{ color: "error.main" }} />
                      )}
                      <Box>
                        <Typography fontWeight={600} fontSize={14}>
                          {d.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {d.category} · {d.size} · {d.date}
                        </Typography>
                      </Box>
                    </Stack>
                  }
                  sx={{ alignItems: "center", m: 0 }}
                />
              </Paper>
            ))}
          </FormGroup>
          {docs.length > 0 && !hasDocSelection ? (
            <Typography variant="caption" color="error">
              Select at least one document to reject.
            </Typography>
          ) : null}

          <Divider />

          <Typography fontWeight={700} fontSize={14}>
            Rejection Reason
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            placeholder="Please provide specific reasons for rejection. This will be sent to the asset owner..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <Typography variant="caption" color="text.secondary">
            Be specific about what needs to be corrected so the owner can resubmit properly.
          </Typography>
          {errorMessage ? (
            <Alert severity="error" sx={{ mt: 0.5 }}>
              {errorMessage}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={requestClose}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <HighlightOff />}
          onClick={handleConfirm}
          disabled={submitting || !hasDocSelection}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Confirm Rejection
        </Button>
      </DialogActions>
    </Dialog>
  );
}
